import { useEffect, useState } from 'react'
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth'
import { auth, db, getMessagingInstance } from './firebase'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import './App.css'

function App() {
  const [user, setUser] = useState<any>(null)
  const [pushSubscription, setPushSubscription] = useState<PushSubscription | null>(null)
  const [notificationSupported, setNotificationSupported] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    setNotificationSupported('serviceWorker' in navigator && 'PushManager' in window)

    auth.onAuthStateChanged(setUser)

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js').catch((err) => {
        console.error('Service Worker registration failed:', err)
      })
    }
  }, [])

  const handleSignIn = async () => {
    setLoading(true)
    try {
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)
      setUser(result.user)
      setMessage('✓ Signed in successfully')
    } catch (error: any) {
      setMessage(`✗ Sign-in failed: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    setLoading(true)
    try {
      await auth.signOut()
      setUser(null)
      setPushSubscription(null)
      setMessage('✓ Signed out')
    } catch (error: any) {
      setMessage(`✗ Sign-out failed: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleSubscribeToPush = async () => {
    if (!user || !notificationSupported) {
      setMessage('✗ Cannot subscribe to push notifications')
      return
    }

    setLoading(true)
    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setMessage('✗ Notification permission denied')
        setLoading(false)
        return
      }

      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          import.meta.env.VITE_VAPID_PUBLIC_KEY || ''
        ),
      })

      setPushSubscription(subscription)

      // Save subscription to Firestore for the worker to find later
      await setDoc(
        doc(db, 'notification_spike_subscriptions', user.uid),
        {
          subscription: {
            endpoint: subscription.endpoint,
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(subscription.getKey('auth')),
          },
          userId: user.uid,
          userEmail: user.email,
          createdAt: new Date(),
        }
      )

      setMessage('✓ Push subscription successful')
    } catch (error: any) {
      setMessage(`✗ Push subscription failed: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="app">
      <header>
        <h1>🔔 Chore PWA - Notification Spike</h1>
        <p>Testing Web Push through Cloudflare</p>
      </header>

      <main>
        {!user ? (
          <section className="auth-section">
            <h2>Step 1: Sign In</h2>
            <button onClick={handleSignIn} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign in with Google'}
            </button>
          </section>
        ) : (
          <>
            <section className="user-section">
              <h2>Signed in as</h2>
              <p className="user-email">{user.email}</p>
              <button onClick={handleSignOut} disabled={loading}>
                Sign out
              </button>
            </section>

            {notificationSupported ? (
              <section className="push-section">
                <h2>Step 2: Enable Push Notifications</h2>
                <p>
                  {pushSubscription
                    ? '✓ Push notifications are active'
                    : 'Click below to enable push notifications'}
                </p>
                <button
                  onClick={handleSubscribeToPush}
                  disabled={loading || !!pushSubscription}
                >
                  {pushSubscription ? 'Already subscribed' : 'Enable notifications'}
                </button>
              </section>
            ) : (
              <section className="push-section error">
                <h2>⚠️ Push Not Supported</h2>
                <p>Your browser doesn't support Web Push notifications.</p>
                <p>Try Chrome, Firefox, or Edge on Android/Desktop.</p>
              </section>
            )}

            {pushSubscription && (
              <section className="status-section">
                <h2>Subscription Status</h2>
                <p>✓ Service Worker registered</p>
                <p>✓ Push subscription active</p>
                <p>✓ Subscription saved to Firestore</p>
                <p className="hint">
                  The Cloudflare Worker will now pick up this subscription on its next cron run
                  and send a test notification.
                </p>
              </section>
            )}
          </>
        )}

        {message && <div className="message">{message}</div>}
      </main>

      <footer>
        <p>Spike Goal: Verify Web Push works through Cloudflare's free tier</p>
      </footer>
    </div>
  )
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return new Uint8Array([...rawData].map((char) => char.charCodeAt(0)))
}

function arrayBufferToBase64(buffer: ArrayBuffer | null) {
  if (!buffer) return ''
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

export default App
